
import React, { Suspense } from "react";
import { Outlet, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "./lib/utils";
import { FaDiscord, FaTwitch, FaTwitter } from 'react-icons/fa';
import LoaderScreen from './components/LoaderScreen';

const FloatingBlob = React.memo(() => (
  <div className="absolute right-[-50px] top-1/4 w-[40px] h-[40px] sm:w-[50px] sm:h-[50px] md:w-[60px] md:h-[60px] rounded-full mix-blend-normal dark:mix-blend-plus-lighter filter dark:blur-lg blur-md animate-float-right">
    <div
      className="absolute inset-0 rounded-full bg-gradient-to-b from-deep-royal-blue/70 via-tropical-teal/50 to-crystal-sky/40 
      dark:from-deep-royal-blue/80 dark:via-tropical-teal/60 dark:to-crystal-sky/50 animate-morph"
    />
  </div>
));

const Footer = React.memo(() => (
  <footer className="relative z-10 mt-auto">
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="text-sm text-muted-foreground order-2 sm:order-1">
          &copy; {new Date().getFullYear()} STATBOX. All rights reserved.
        </p>
        <div className="flex items-center gap-6 order-1 sm:order-2">
          <div className="flex items-center gap-4">
            <a 
              href="https://discord.gg/statbox" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-tropical-teal transition-colors hover:scale-110"
            >
              <FaDiscord size={18} />
            </a>
            <a 
              href="https://twitch.tv/statbox" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-tropical-teal transition-colors hover:scale-110"
            >
              <FaTwitch size={18} />
            </a>
            <a 
              href="https://twitter.com/statbox" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-tropical-teal transition-colors hover:scale-110"
            >
              <FaTwitter size={18} />
            </a>
          </div>
          <div className="h-4 w-px bg-border/50" />
          <div className="flex space-x-6">
          <Link
            to="/privacy"
              className="text-sm text-muted-foreground hover:text-tropical-teal transition-colors"
            >
              Privacy Policy
            </Link>
          <Link
            to="/terms"
              className="text-sm text-muted-foreground hover:text-tropical-teal transition-colors"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </div>
  </footer>
));

const Header = React.memo(() => (
  <motion.header
    initial={{ y: -100 }}
    animate={{ y: 0 }}
    transition={{ type: "spring", stiffness: 100 }}
    className="fixed top-0 left-0 right-0 z-50"
  >
    <div className="container mx-auto px-4 pt-2">
      <div className="flex h-16 items-center"> 
        <motion.div
          whileHover={{ scale: 1.05 }}
          className="flex items-center"
        >
          <Link to="/" className="flex items-center" aria-label="Home">
            <img
              src="/logo_white.png"
              alt="Logo"
              className="h-6 w-auto object-contain"
            />
          </Link>
        </motion.div>
      </div>
    </div>
  </motion.header>
));

export default function PreLoginLayout({ className }) {
  return (
    <div
      className={cn(
        "min-h-screen dark bg-background font-cantroe text-foreground relative overflow-hidden transition-colors duration-300",
        className
      )}
    >
      <div className="fixed inset-0 z-0 bg-background">
        <div className="absolute -left-1/4 top-0 w-[400px] h-[300px] sm:w-[600px] sm:h-[400px] md:w-[700px] md:h-[500px] lg:w-[800px] lg:h-[600px] xl:w-[900px] xl:h-[700px] rounded-[60%_40%_70%_30%] mix-blend-normal dark:mix-blend-plus-lighter filter dark:blur-2xl blur-xl animate-blob-vertical">
          <div
            className="absolute inset-0 rounded-[60%_40%_70%_30%] bg-gradient-to-b from-deep-royal-blue/90 via-tropical-teal/70 to-crystal-sky/60 
            dark:from-deep-royal-blue dark:via-tropical-teal dark:to-crystal-sky/90 animate-morph"
          />
          <div
            className="absolute inset-4 rounded-[40%_60%_30%_70%] bg-gradient-to-b from-deep-royal-blue/80 via-tropical-teal/60 to-crystal-sky/50 
            dark:from-deep-royal-blue/90 dark:via-tropical-teal/80 dark:to-crystal-sky/70 animate-morph animation-delay-2000"
          />
          <div
            className="absolute inset-8 rounded-[70%_30%_60%_40%] bg-gradient-to-b from-deep-royal-blue/70 via-tropical-teal/50 to-crystal-sky/40 
            dark:from-deep-royal-blue/80 dark:via-tropical-teal/70 dark:to-crystal-sky/60 animate-morph animation-delay-4000"
          />
        </div>
        <FloatingBlob />
      </div>

      <Header />

      {/* Main Content */}
      <main className="relative pt-20 min-h-screen z-10">
        <Suspense fallback={<LoaderScreen />}>
          <Outlet />
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}
